// #region Imports
import BadgeIcon from "@mui/icons-material/Badge";
import MessageIcon from "@mui/icons-material/Message";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { DialogContext } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { Currency, formatCurrency } from "_shared/money/currencies/currency";
import Money from "_shared/money/money";
import Name from "_shared/names/name";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import { useGetIndividualAvatarQuery, useGetIndividualByIdQuery, useGetIndividualsByUserIdsQuery, individualsApi } from "marketplace/individuals/_api/individual-api";
import {
  getIndividualAvatar,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import {
  ProfileFieldDensity,
  useProfileFieldDensity,
} from "marketplace/individuals/profiles/_views/individual-profile-density";
import InviteToTeamButton from "marketplace/teams/invitations/_views/invite-to-team-button";
import ChatDialog from "messaging/forums/_views/components/forum-chat-dialog";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { documentDownloadFailureMessage } from "documents/_api/document-download-failure";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import {
  useCreateChatForumForNetworkConnectionMutation,
  useGetChatForumForNetworkConnectionQuery
} from "users/networking/connections/_api/user-network-connection-api";
import useNetworkConnectionStatus from "users/networking/connections/_hooks/use-network-connection-status";
import { getNetworkConnectionByUserId } from "users/networking/connections/_selectors/user-network-connection-selectors";
import NetworkButton from "users/networking/connections/_views/components/network-button";
import NetworkConnectionNoteEditor from "users/networking/connections/notes/_views/network-connection-note-editor";
import { getNetworkConnectionChatForum } from "users/networking/connections/network-connection-chat-forum";
import DirectProposalButton from "work/proposals/_views/_buttons/direct-proposal-button";
import AddToWorkButton from "work/staffing/_views/add-to-work-button";
import startDraftProposalWithVendorTeamLeader from "work/proposals/_views/drafts/start-draft-proposal-with-vendor-team-leader";
// #endregion

/** How many lines of credentials are shown before the rest is put behind a "Show more" toggle. */
const DESCRIPTION_CLAMPED_LINES = 3;

// #region Styled Components
/**
 * Which props the field layout keeps to itself rather than handing to the DOM.
 *
 * Naming `shouldForwardProp` replaces MUI's own filter outright rather than adding to it, so `sx`
 * has to be named here as well: without it, a field carrying one would put `sx` on its `div` and
 * React would warn about an attribute it does not know.
 */
function isFieldLayoutProp(prop: string): boolean {
  return prop !== "density" && prop !== "sx";
}
const MainContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "chatOpen",
})<{ chatOpen: boolean }>(({ theme, chatOpen }) => ({
  boxSizing: "border-box",
  display: "grid",
  gridTemplateRows: chatOpen ? "auto minmax(0, 1fr)" : "auto 1fr",
  height: chatOpen ? "min(75vh, calc(100vh - 160px))" : undefined,
  minHeight: chatOpen ? 0 : undefined,
  paddingBottom: chatOpen ? 0 : theme.spacing(8),
  width: "100%",
}));
const HeaderContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    padding: theme.spacing(3, 0),
  },
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gridColumn: "1 / auto",
  gridRowStart: "1",
  justifyContent: "center",
  padding: theme.spacing(3),
  paddingTop: theme.spacing(1),
  position: "sticky",
  top: 0,
  width: "100%",
  zIndex: 10,
}));
const ChatContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    minWidth: "unset",
    padding: 0,
  },
  [theme.breakpoints.up("md")]: {
    minWidth: "500px",
    padding: theme.spacing(0, 1),
  },
  [theme.breakpoints.up("lg")]: {
    minWidth: "500px",
    padding: theme.spacing(0, 4),
  },
  display: "flex",
  flexDirection: "column",
  gridColumn: "1 / auto",
  gridRowStart: "2",
  gridRowEnd: "none",
  height: "100%",
  minHeight: 0,
  overflow: "hidden",
}));
/**
 * The flow the fields are laid out in.
 *
 * Two columns where there is room for four tracks of text across, and one where there is not. The
 * compact flow also gives back the wide margin, because 32px either side of a 516px pane is 64px
 * the fields could have had. See `individual-profile-density` for where the threshold comes from.
 */
const ContentContainer = styled("div", {
  shouldForwardProp: isFieldLayoutProp,
})<{ density: ProfileFieldDensity }>(({ theme, density }) => ({
  [theme.breakpoints.down("sm")]: {
    columnCount: 1,
  },
  [theme.breakpoints.up("md")]: {
    columnCount: density === "compact" ? 1 : 2,
    margin: theme.spacing(0, 1),
  },
  [theme.breakpoints.up("lg")]: {
    margin: density === "compact" ? theme.spacing(0, 1) : theme.spacing(0, 4),
  },
  columnFill: "balance",
  columnGap: theme.spacing(1),
  gridColumn: "1 / auto",
  gridRowStart: "2",
}));
/**
 * The private note runs across every column of the fields, below them, so the profile reads first and
 * the note stays out of the way until it is used.
 */
const NoteContainer = styled("div")(({ theme }) => ({
  breakInside: "avoid",
  columnSpan: "all",
  marginTop: theme.spacing(1),
}));
const HeaderInnerContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    justifyItems: "center",
  },
  [theme.breakpoints.up("sm")]: {
    gridTemplateColumns: "min-content minmax(0, 1fr)",
    gridTemplateRows: "min-content auto",
  },
  alignItems: "flex-start",
  gap: theme.spacing(2),
  display: "grid",
  width: "100%",
}));
const TitleContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));
/**
 * The header actions are a network button (which renders a button group), a message button wrapped
 * in a tooltip span, and the proposal buttons. Selecting them together is what keeps the row even.
 */
const HEADER_ACTIONS = "& > .MuiButton-root, & > .MuiButtonGroup-root, & > span";
/** Width, in theme spacing units, that every header action is laid out at. */
const HEADER_ACTION_WIDTH = 25;

const ButtonContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(1),
    justifyContent: "center",
    width: "100%",
    [HEADER_ACTIONS]: {
      flex: "1 1 100%",
      minWidth: 0,
    },
  },
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  justifyContent: "flex-start",
  marginTop: theme.spacing(1),
  // The row owns the spacing and the width of every action, so that buttons carrying margins or a
  // minimum width of their own for other layouts cannot skew this one.
  [HEADER_ACTIONS]: {
    margin: 0,
    minWidth: theme.spacing(HEADER_ACTION_WIDTH),
  },
  "& > .MuiButtonGroup-root, & > span": {
    display: "inline-flex",
  },
  "& > span > .MuiButton-root": {
    margin: 0,
    width: "100%",
  },
}));
/**
 * One field: its label and its value.
 *
 * Side by side where the flow column can hold both, and the label over the value where it cannot.
 * Stacking is what buys the long labels their one line: `Collaborates With Other Individuals` is
 * about 255px, which is more than half of a 516px pane once its chrome is off, and beside a value
 * it wrapped to three lines. The cost is a line each for the short booleans -- `On Call` / `No`
 * pairs well and now does not -- and that is the trade, taken because nothing here is a boolean
 * that a reader scans for and the long fields are the ones they read.
 *
 * It is also the arrangement the profile already uses on a phone, so the compact pane introduces no
 * shape a reader has not seen; it is reached from the width of the pane rather than the width of
 * the window.
 */
const GridContainer = styled("div", {
  shouldForwardProp: isFieldLayoutProp,
})<{ density: ProfileFieldDensity }>(({ theme, density }) => ({
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(0.5),
    gridTemplateColumns: "1fr",
    marginBottom: theme.spacing(2.5),
  },
  display: "grid",
  gridTemplateColumns: density === "compact" ? "1fr" : "1fr 1fr",
  gap: density === "compact" ? theme.spacing(0.5) : theme.spacing(2),
  marginBottom: density === "compact" ? theme.spacing(2.5) : theme.spacing(2),
  width: "100%",
  "& > *": {
    minWidth: 0,
    overflowWrap: "anywhere",
  },
}));
const StyledAvatar = styled(Avatar)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    height: theme.spacing(7),
    width: theme.spacing(7),
  },
  height: theme.spacing(15),
  width: theme.spacing(15),
}));
const FieldTitle = styled(Typography)(() => ({
  fontWeight: 500,
}));
const ProfileName = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    fontSize: theme.typography.h5.fontSize,
    textAlign: "center",
  },
  overflowWrap: "anywhere",
}));
const ProfileTypeChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.text.disabled,
  margin: theme.spacing(0.25),
}));
const NameChipContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  alignItems: "flex-start",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  justifyContent: "flex-start",
}));
const DescriptionContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    margin: theme.spacing(1, 0),
    width: "100%",
  },
  [theme.breakpoints.up("sm")]: {
    gridColumn: "1 / span 2",
    gridRow: "2",
    margin: theme.spacing(2, 0),
  },
  display: "flex",
  flexDirection: "column",
  textAlign: "left",
  width: "100%",
}));
const DescriptionText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ theme, expanded }) => ({
  [theme.breakpoints.down("sm")]: {
    textAlign: "center",
  },
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: expanded ? "unset" : DESCRIPTION_CLAMPED_LINES,
  overflow: expanded ? "visible" : "hidden",
  overflowWrap: "anywhere",
  fontSize: "1.1em",
  fontStyle: "italic",
  lineHeight: "1.4em",
}));
const DescriptionToggle = styled(Button)(({ theme }) => ({
  alignSelf: "flex-start",
  fontStyle: "normal",
  minWidth: 0,
  padding: 0,
  textTransform: "none",
  [theme.breakpoints.down("sm")]: {
    alignSelf: "center",
  },
}));
const DescriptionDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%",
}));
const ResumeLink = styled(Link)(() => ({
  cursor: "pointer",
}));
const CompanyProfileLink = styled(Link)(() => ({
  cursor: "pointer",
  overflowWrap: "anywhere",
}));
/**
 * An email address, which is one word however long it is.
 *
 * The field grid lets its values break anywhere, which is right for a sentence and wrong for an
 * address: at 516px it produced `client.membe` / `r.2@example.` / `com`, three lines of something
 * that is not English and cannot be read back. There is no break opportunity inside an address to
 * wrap at instead, so an address too long for its track is cut off at the end, where a reader can
 * see that it has been -- with the whole of it on the element's title and in the `mailto:` it
 * links to, so nothing is lost. `&&` because the rule this overrides is the grid's own, and the
 * two would otherwise be tied on specificity.
 */
const EmailLink = styled(Link)(() => ({
  "&&": {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));
const MessageButtonContainer = styled("span")<{ networked: boolean }>(({ networked: inNetwork }) => ({
  cursor: inNetwork ? "pointer" : "not-allowed",
}));
// #endregion

/**
 * Whether the credentials run past the lines they are clamped to, at the width they are drawn at.
 *
 * A character count cannot answer this: 300 characters are five lines in a narrow pane and two
 * across a wide dialog, and a toggle over text that is already whole offers nothing to show. So
 * the box is measured, in either state -- its full height against the clamp's -- and again whenever
 * its width changes, which can add a cut or take one away. False until measured, which in
 * jsdom (no ResizeObserver) is never, and draws no toggle.
 */
function useDescriptionOverflows(credentials: string) {
  const ref = useRef<HTMLElement>(null);
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    // Reports once on `observe`, and again on every resize. Keyed on the text as well, because new
    // text in a clamped box can overflow without changing the box's size.
    const observer = new ResizeObserver(() => {
      const lineHeight = Number.parseFloat(getComputedStyle(element).lineHeight);
      if (!Number.isFinite(lineHeight)) return;
      // A pixel of slack for the sub-pixel rounding of `1.4em`.
      setOverflows(element.scrollHeight > lineHeight * DESCRIPTION_CLAMPED_LINES + 1);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [credentials]);
  return { ref, overflows };
}

function getBillingRateDisplayValue(billingRate: string): string {
  const sanitizedBillingRate = billingRate.replace(/[$,\s]/g, "");

  if (!/^-?(?:\d+|\d*\.\d+)$/.test(sanitizedBillingRate)) {
    return billingRate;
  }

  const parsedBillingRate = Number.parseFloat(sanitizedBillingRate);

  if (!Number.isFinite(parsedBillingRate)) {
    return billingRate;
  }

  return formatCurrency(new Money(parsedBillingRate, Currency.USD));
}

export interface ViewIndividualProfileProps {
  profile?: IndividualProfile;
  individualId?: Guid;
  userId?: Guid;
  proposalTarget?: {
    userId: Guid;
    legalEntityId: Guid;
    hasSigningAuthority?: boolean;
  };
}

export default function ViewIndividualProfile(props: Readonly<ViewIndividualProfileProps>) {
  const {
    profile,
    individualId,
    userId,
    proposalTarget,
  } = props;

  // #region State
  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const [activeChatForum, setActiveChatForum] = useState<Forum | undefined>();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState<boolean>(false);

  // #region Hooks
  // How much room the profile was given, which is not the same question as how wide the window is:
  // the staffing picker takes a column of this dialog and leaves the profile 516px of the same
  // window that holds it whole everywhere else.
  const fieldDensity = useProfileFieldDensity();
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const dialog = useContext(DialogContext);
  const navigate = useNavigate();

  // #region Queries
  const individualByIdQuery = useGetIndividualByIdQuery(
    individualId ? { individualId: individualId.value } : skipToken,
    { skip: !!profile || !individualId }
  );

  const individualByUserIdsQuery = useGetIndividualsByUserIdsQuery(
    userId ? { userIds: [userId.value] } : skipToken
  );

  const individual = useMemo(() => {
    let individualData = individualByIdQuery.data;
    if(!individualData && individualByUserIdsQuery.data?.length) {
      individualData = individualByUserIdsQuery.data[0];
    }
    if (!individualData) return null;
    return Individual.fromJSON(individualData);
  }, [individualByUserIdsQuery.data, individualByIdQuery.data]);

  const individualProfile = profile ?? individual?.profile;
  const company = individual?.company;
  const companyId = company?.id;
  const companyName = company?.profile?.name;
  const proposalTargetUserId = proposalTarget?.userId ?? individual?.userId ?? individualProfile?.userId;
  const proposalTargetLegalEntityId =
    proposalTarget?.legalEntityId ?? individual?.legalEntityId ?? individualProfile?.legalEntityId;
  const proposalTargetHasSigningAuthority = proposalTarget?.hasSigningAuthority ?? individual?.hasSigningAuthority;
  const proposalTargetFirstName = individualProfile?.firstName;
  const proposalTargetLastName = individualProfile?.lastName;
  const proposalTargetName = useMemo(() => {
    if (!proposalTargetFirstName || !proposalTargetLastName) return undefined;
    return Name.create({
      params: {
        firstName: proposalTargetFirstName,
        lastName: proposalTargetLastName,
      },
    });
  }, [proposalTargetFirstName, proposalTargetLastName]);
  const viewingOwnProfile = Boolean(
    individualId?.isEqualTo(session.user?.individualId) || proposalTargetUserId?.isEqualTo(session.user?.id),
  );
  // The marketplace individual behind this profile, which is what a team invitation names. The
  // profile is reached both by individual id and by user id, so take whichever route brought us
  // here. Without one there is nobody to invite, and the action stays away.
  const inviteeIndividualId = individual?.id ?? individualId;
  const canCreateProposalForTarget = Boolean(
    proposalTargetUserId &&
    proposalTargetLegalEntityId &&
    proposalTargetName &&
    !viewingOwnProfile &&
    session.user?.legalEntityId?.isEqualTo(proposalTargetLegalEntityId) === false,
  );

  const networkForumQuery = useGetChatForumForNetworkConnectionQuery(
    individualProfile?.userId ? { userId: individualProfile.userId } : skipToken
  );
  const [createChat, createChatResult] = useCreateChatForumForNetworkConnectionMutation();


  const chat = useMemo(() => {
    return getNetworkConnectionChatForum(networkForumQuery.data ?? createChatResult.data);
  }, [
    createChatResult.data,
    networkForumQuery.data,
  ]);
  const currentChatForum = activeChatForum ?? chat ?? undefined;

  // #region Selectors
  const avatar = useSelector(getIndividualAvatar(individualId ?? null, "small"));
  useGetIndividualAvatarQuery(
    individualId ? { individualId: individualId.value, size: "small" } : skipToken,
    { skip: !individualProfile?.avatarId || !!avatar },
  );
  const networkConnection = useSelector(individualProfile?.userId
    ? getNetworkConnectionByUserId(individualProfile.userId)
    : () => null
  );
  const isNetworkedWith = useNetworkConnectionStatus(individualProfile?.userId) === true;
  const canMessage = isNetworkedWith || !!currentChatForum;
  const credentials = individualProfile?.credentials ?? "";
  const { ref: descriptionRef, overflows: descriptionOverflows } = useDescriptionOverflows(credentials);

  // A long description stays collapsed when the dialog moves to a different person — one profile's
  // "Show more" should not decide how the next one opens.
  //
  // Uses React's "adjust state during render" pattern rather than an effect, so the first paint of a
  // new profile is already collapsed and no cascading effect render is scheduled.
  const shownProfileKey = individualProfile?.userId?.value ?? null;
  const [lastShownProfileKey, setLastShownProfileKey] = useState<string | null>(shownProfileKey);
  if (shownProfileKey !== lastShownProfileKey) {
    setLastShownProfileKey(shownProfileKey);
    setIsDescriptionExpanded(false);
  }

  useEffect(() => {
    if (individualByIdQuery.isError) {
      enqueueSnackbar("Unable to load profile", { variant: "error" });
    }
  }, [individualByIdQuery.isError]);

  // #endregion

  async function downloadResume() {
    if (!individualProfile?.resumeId || !individualId) return;
    try {
      const { blob, filename } = await dispatch(
        individualsApi.endpoints.downloadIndividualResume.initiate({ individualId: individualId.value })
      ).unwrap();
      const link = document.createElement("a");
      link.setAttribute("href", globalThis.URL.createObjectURL(blob));
      if (filename) link.setAttribute("download", filename);
      link.setAttribute("target", "_blank");
      link.click();
    } catch (error) {
      // A refusal says why, such as a resume still being scanned for malware (#6994).
      enqueueSnackbar(documentDownloadFailureMessage(error, "Unable to download resume"), { variant: "error" });
    }
  }

  function hideIfNotIPAttorney() {
    return {
      display: individualProfile?.categories?.includes("IPAttorney") ? "grid" : "none",
    };
  }

  async function createForumForNetworkConnection(): Promise<Forum | null> {
    if (chat) return chat;
    if (!networkConnection?.connectionId) return null;

    try {
      const forumData = await createChat({ connectionId: networkConnection.connectionId }).unwrap();
      const nextForum = getNetworkConnectionChatForum(forumData);
      if (!nextForum) {
        throw new Error("Unable to create chat forum");
      }

      setActiveChatForum(nextForum);
      return nextForum;
    } catch {
      enqueueSnackbar("Unable to create chat forum", { variant: "error" });
      return null;
    }
  }

  function openCompanyProfile(event: React.MouseEvent) {
    if (!companyId || !dialog?.openDialog) return;

    event.preventDefault();
    dialog.openDialog({
      component: <ViewCompanyProfile companyId={companyId} />,
      titleStyle: {
        paddingBottom: 0,
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
  }

  function handleSendProposalToTarget(targetUrl: URL): void {
    if (
      targetUrl.pathname === "/proposals/drafts/create" &&
      proposalTargetUserId &&
      proposalTargetLegalEntityId
    ) {
      startDraftProposalWithVendorTeamLeader({
        dispatch,
        leader: {
          userId: proposalTargetUserId,
          legalEntityId: proposalTargetLegalEntityId,
        },
        navigate,
        session,
      });
      return;
    }

    navigate(targetUrl.pathname + targetUrl.search);
  }

  function renderProposalButtons() {
    if (!canCreateProposalForTarget || !proposalTargetUserId || !proposalTargetLegalEntityId || !proposalTargetName) {
      return null;
    }

    return (
      <DirectProposalButton
        targetUserId={proposalTargetUserId}
        targetEntityId={proposalTargetLegalEntityId}
        targetName={proposalTargetName}
        targetHasSigningAuthority={proposalTargetHasSigningAuthority}
        onClick={handleSendProposalToTarget}
        useStyledButton={true}
      />
    );
  }

  // #region Render
  return (
    <MainContainer chatOpen={chatOpen}>
      <HeaderContainer>
        <HeaderInnerContainer>
          <StyledAvatar src={avatar} />
          <TitleContainer>
            <NameChipContainer>
              <ProfileName variant="h4">
                {individualProfile?.firstName} {individualProfile?.lastName}
              </ProfileName>
              <span>
                {individualProfile?.categories?.map((category) => (
                  <ProfileTypeChip key={category} label={category} size="small" />
                ))}
              </span>
            </NameChipContainer>
            {individualProfile?.userId && (
              <ButtonContainer>
                <NetworkButton userId={individualProfile?.userId} />
                <Tooltip
                  title={(function () {
                    if (chatOpen) return "Back to profile";
                    if (canMessage) return currentChatForum ? "Send message" : "Start conversation";
                    if (viewingOwnProfile) return "You can't send a message to yourself";
                    return "You must be in the user's network to send a message";
                  })()}
                >
                  <MessageButtonContainer networked={canMessage}>
                    <Button
                      variant="contained"
                      startIcon={chatOpen ? <BadgeIcon /> : <MessageIcon />}
                      disableElevation
                      disabled={!canMessage || viewingOwnProfile}
                      color="primary"
                      onClick={async () => {
                        if (chatOpen) {
                          setChatOpen(false);
                          return;
                        }

                        const nextForum = currentChatForum ?? await createForumForNetworkConnection();
                        if (!nextForum) return;

                        setActiveChatForum(nextForum);
                        setChatOpen(true);
                      }}
                    >
                      {chatOpen ? "Back to profile" : "Message"}
                    </Button>
                  </MessageButtonContainer>
                </Tooltip>
                {!chatOpen && renderProposalButtons()}
                {!chatOpen && !viewingOwnProfile && inviteeIndividualId && (
                  <InviteToTeamButton inviteeIndividualId={inviteeIndividualId} />
                )}
                {!chatOpen && !viewingOwnProfile && proposalTargetUserId && proposalTargetLegalEntityId && (
                  <AddToWorkButton
                    candidateUserId={proposalTargetUserId}
                    candidateLegalEntityId={proposalTargetLegalEntityId}
                    // Who this is, for the widths where the picker leaves room for a context rail
                    // and no more. The same three things the header leads with, because the rail
                    // stands in for the header at those widths.
                    candidateName={`${individualProfile?.firstName ?? ""} ${individualProfile?.lastName ?? ""}`.trim()}
                    candidateCompanyName={companyName}
                    candidateAvatarUrl={avatar}
                  />
                )}
              </ButtonContainer>
            )}
          </TitleContainer>
          <DescriptionContainer>
            <DescriptionText ref={descriptionRef} variant="subtitle1" expanded={isDescriptionExpanded || !descriptionOverflows}>
              {credentials}
            </DescriptionText>
            {descriptionOverflows && (
              <DescriptionToggle
                variant="text"
                size="small"
                onClick={() => setIsDescriptionExpanded((expanded) => !expanded)}
              >
                {isDescriptionExpanded ? "Show less" : "Show more"}
              </DescriptionToggle>
            )}
            <DescriptionDivider />
          </DescriptionContainer>
        </HeaderInnerContainer>
      </HeaderContainer>
      <ChatContainer>
        {individualByIdQuery.isLoading && <Loader />}
        {chatOpen && currentChatForum && (
          <ChatDialog
            fillAvailableSpace
            loadedForum={currentChatForum}
          />
        )}
      </ChatContainer>
      <ContentContainer data-field-density={fieldDensity} density={fieldDensity}>
        {!chatOpen && !individualByIdQuery.isLoading && (
          <>
            {individualProfile?.professionalRegistration && (
              <GridContainer density={fieldDensity} sx={hideIfNotIPAttorney()}>
                <FieldTitle>Professional Registration #</FieldTitle>
                <Typography>{individualProfile?.professionalRegistration?.value}</Typography>
              </GridContainer>
            )}
            {individualProfile?.regionsRegistered && individualProfile.regionsRegistered.length > 0 && (
              <GridContainer density={fieldDensity} sx={hideIfNotIPAttorney()}>
                <FieldTitle>Regions Registered</FieldTitle>
                <Typography>{individualProfile?.regionsRegistered?.join(", ")}</Typography>
              </GridContainer>
            )}
            {(individualProfile?.technicalBackgrounds?.length ?? 0) > 0 && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Technical Background</FieldTitle>
                <Typography>{individualProfile?.technicalBackgrounds?.join(", ")}</Typography>
              </GridContainer>
            )}
            {(individualProfile?.practiceAreas?.length ?? 0) > 0 && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Practice Area</FieldTitle>
                <Typography>{individualProfile?.practiceAreas?.join(", ")}</Typography>
              </GridContainer>
            )}
            {companyId && companyName && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Company</FieldTitle>
                <CompanyProfileLink
                  href={`/marketplace/company/${companyId.value}`}
                  onClick={openCompanyProfile}
                >
                  {companyName}
                </CompanyProfileLink>
              </GridContainer>
            )}
            {individualProfile?.school && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Undergraduate School / Law School</FieldTitle>
                <Typography>{individualProfile?.school}</Typography>
              </GridContainer>
            )}
            {individualProfile?.degree && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Degree(s)</FieldTitle>
                <Typography>{individualProfile?.degree}</Typography>
              </GridContainer>
            )}
            {individualProfile?.numberOfApplicationsDraftedProsecuted && (
              <GridContainer density={fieldDensity} sx={hideIfNotIPAttorney()}>
                <FieldTitle>Number Applications Drafted / Prosecuted</FieldTitle>
                <Typography>{individualProfile?.numberOfApplicationsDraftedProsecuted}</Typography>
              </GridContainer>
            )}
            {individualProfile?.numberOfAllowedApplications && (
              <GridContainer density={fieldDensity} sx={hideIfNotIPAttorney()}>
                <FieldTitle>Number Allowed Applications</FieldTitle>
                <Typography>{individualProfile?.numberOfAllowedApplications}</Typography>
              </GridContainer>
            )}
            {individualProfile?.barAdmissionYear && (
              <GridContainer density={fieldDensity} sx={hideIfNotIPAttorney()}>
                <FieldTitle>Year Of Bar Admission</FieldTitle>
                <Typography>{individualProfile?.barAdmissionYear}</Typography>
              </GridContainer>
            )}
            {individualProfile?.fluentLanguages && individualProfile.fluentLanguages.length > 0 && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Fluent Languages</FieldTitle>
                <Typography>{individualProfile?.fluentLanguages?.join(", ")}</Typography>
              </GridContainer>
            )}
            {individualProfile?.technicalLanguages && individualProfile.technicalLanguages.length > 0 && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Technical Languages</FieldTitle>
                <Typography>{individualProfile?.technicalLanguages?.join(", ")}</Typography>
              </GridContainer>
            )}
            {individualProfile?.regionsLicensed && individualProfile.regionsLicensed.length > 0 && (
              <GridContainer density={fieldDensity} sx={hideIfNotIPAttorney()}>
                <FieldTitle>States Licensed In</FieldTitle>
                <Typography>{individualProfile?.regionsLicensed?.join(", ")}</Typography>
              </GridContainer>
            )}
            {individualProfile?.insured && (individualProfile?.insured?.insuredAmount ?? 0) > 0 && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Insured Amount</FieldTitle>
                <Typography>
                  {formatCurrency(
                    new Money(
                      Number.parseFloat(individualProfile?.insured?.insuredAmount?.toString() ?? "0"),
                      Currency.USD
                    )
                  )}
                </Typography>
              </GridContainer>
            )}
            {individualProfile?.billingRate?.billingRate && individualProfile?.billingRate?.billingRate !== "" && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Billing Rate</FieldTitle>
                <Typography>{getBillingRateDisplayValue(individualProfile.billingRate.billingRate)}</Typography>
                <FieldTitle>Billing Unit</FieldTitle>
                <Typography>{individualProfile?.billingRate?.billingUnit ?? "N/A"}</Typography>
              </GridContainer>
            )}
            {individualProfile?.phoneNumber?.value && individualProfile.phoneNumber.value !== "" && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Phone Number</FieldTitle>
                <Link href={`tel:${individualProfile?.phoneNumber?.value}`}>
                  {individualProfile?.phoneNumber?.value}
                </Link>
              </GridContainer>
            )}
            {individualProfile?.email?.value && individualProfile?.email?.value !== "" && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Email Address</FieldTitle>
                <EmailLink
                  href={`mailto:${individualProfile?.email?.value}`}
                  title={individualProfile?.email?.value}
                >
                  {individualProfile?.email?.value}
                </EmailLink>
              </GridContainer>
            )}
            <GridContainer density={fieldDensity}>
              <FieldTitle>On Call</FieldTitle>
              {individualProfile?.isOnCall ? "Yes" : "No"}
            </GridContainer>
            <GridContainer density={fieldDensity}>
              <FieldTitle>Potential Diversity Candidate</FieldTitle>
              {individualProfile?.potentialDiversityCandidate ? "Yes" : "No"}
            </GridContainer>
            <GridContainer density={fieldDensity}>
              <FieldTitle>Collaborates With Other Individuals</FieldTitle>
              {individualProfile?.collaborates ? "Yes" : "No"}
            </GridContainer>
            {individualProfile?.professionalPublications && individualProfile?.professionalPublications.length > 0 && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Professional Publications</FieldTitle>
                {individualProfile?.professionalPublications?.map((publication) => (
                  <Link href={publication} key={publication} target="_blank" rel="noopener">
                    {publication}
                  </Link>
                ))}
              </GridContainer>
            )}
            {individualProfile?.sampleApplications && individualProfile?.sampleApplications.length > 0 && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Sample Applications</FieldTitle>
                {individualProfile?.sampleApplications?.map((application) => (
                  <Link href={application} key={application} target="_blank" rel="noopener">
                    {application}
                  </Link>
                ))}
              </GridContainer>
            )}
            {individualProfile?.links && individualProfile?.links.length > 0 && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Other Links</FieldTitle>
                {individualProfile?.links?.map((link) => (
                  <Link href={link} key={link} target="_blank" rel="noopener">
                    {link}
                  </Link>
                ))}
              </GridContainer>
            )}
            {individual?.profile.resumeId && (
              <GridContainer density={fieldDensity}>
                <FieldTitle>Resume</FieldTitle>
                <ResumeLink onClick={downloadResume}>View resume</ResumeLink>
              </GridContainer>
            )}
            {!viewingOwnProfile && networkConnection?.connectionId && (
              <NoteContainer>
                <NetworkConnectionNoteEditor connectionId={networkConnection.connectionId} />
              </NoteContainer>
            )}
          </>
        )}
      </ContentContainer>
    </MainContainer>
  );
}
